import {
    DndContext,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    type DragEndEvent,
} from '@dnd-kit/core';
import {
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import type { ReportPromptReordering } from '../hooks/useReportPromptOrdering';
import { detectPromptGroupCollision } from '../utils/detectPromptGroupCollision';
import {
    REPORT_EVENT_GROUP_EMPTY_KEY,
    type ReportEventGroup,
} from '../utils/groupReportEvents';
import { resolveDragEndMove } from '../utils/resolveDragEndMove';
import { SortableReportPromptGroup } from './SortableReportPromptGroup';

interface SortableReportPromptGroupsProps {
    groups: ReportEventGroup[];
    disabled: boolean;
    reordering: ReportPromptReordering;
    renderGroup: (group: ReportEventGroup, index: number) => ReactNode;
}

export function SortableReportPromptGroups({
    groups,
    disabled,
    reordering,
    renderGroup,
}: SortableReportPromptGroupsProps) {
    const { t } = useTranslation(I18nNamespace.REPORTS);
    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        }),
    );

    // Events without a prompt share one bucket, so that group has no single
    // prompt whose position could be moved — and it cannot be a drop target
    // either, so a lone prompt beside it has nowhere to go.
    const movablePromptCount = groups.filter(
        (group) => group.key !== REPORT_EVENT_GROUP_EMPTY_KEY,
    ).length;

    const isReorderable = (group: ReportEventGroup) =>
        !disabled &&
        !reordering.isPending &&
        movablePromptCount > 1 &&
        group.key !== REPORT_EVENT_GROUP_EMPTY_KEY;

    const promptLabelFor = (id: string | number) =>
        groups.find((group) => group.key === String(id))?.sectionLabel ?? '';

    const handleDragEnd = (dragEnd: DragEndEvent) => {
        const move = resolveDragEndMove(
            dragEnd,
            groups.map((group) => group.key),
        );
        if (!move) {
            return;
        }
        reordering.move(move.sourceId, move.targetId, move.placement);
    };

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={detectPromptGroupCollision}
            onDragEnd={handleDragEnd}
            accessibility={{
                announcements: {
                    onDragStart: ({ active }) =>
                        t('detail.promptOrder.pickedUp', {
                            name: promptLabelFor(active.id),
                        }),
                    onDragOver: ({ over }) =>
                        over
                            ? t('detail.promptOrder.movedOver', {
                                  name: promptLabelFor(over.id),
                              })
                            : undefined,
                    onDragEnd: ({ over }) =>
                        over
                            ? t('detail.promptOrder.dropped', {
                                  name: promptLabelFor(over.id),
                              })
                            : t('detail.promptOrder.cancelled'),
                    onDragCancel: () => t('detail.promptOrder.cancelled'),
                },
            }}
        >
            <SortableContext
                items={groups.map((group) => group.key)}
                strategy={verticalListSortingStrategy}
            >
                <div className="flex flex-col gap-2">
                    {groups.map((group, index) => (
                        <SortableReportPromptGroup
                            key={group.key}
                            id={group.key}
                            reorderable={isReorderable(group)}
                            reorderLabel={t('detail.promptOrder.handle', {
                                name: group.sectionLabel,
                            })}
                        >
                            {renderGroup(group, index)}
                        </SortableReportPromptGroup>
                    ))}
                </div>
            </SortableContext>
        </DndContext>
    );
}
